/**
 * Per-project view state (W1-09 state-store; contract src/state/types.ts, ARCHITECTURE.md §5.5, DESIGN.md §4.5).
 *
 * Pure snapshot / restore / validation; no persistence here (the 300 ms debounced save is src/app/viewStatePersist.ts,
 * W1-16). The persisted JSON is opaque to persist; `toJson` tags it with a format version and `parse` validates it:
 * wrong types, unknown enum values or another format version give null (the project then opens with defaults),
 * while numbers that are merely out of range are clamped (zoom, opacity, snap divisor...).
 *
 * Also the shared normalisers of the editor store's view setters (`normalizeSnap`, `normalizeTimeline`,
 * `normalizeFilters`), so a value set live and a value restored from disk obey the same bounds.
 */

import type { Json } from '../core/util/types';
import type { ChartDoc, LineId, LineProp } from '../core/model/types';
import type { TempoEditPolicy } from '../core/commands/types';
import { LINE_PROPS } from '../core/model/props';
import type {
  ColumnRef,
  EditorStoreState,
  PersistedViewState,
  SnapState,
  TimelineViewState,
  ViewFilterState,
  ViewStateApi,
  WorkspaceId,
} from './types';

/** Format version written by `toJson`; other versions are refused by `parse`. */
export const VIEW_STATE_VERSION = 1;

export const WORKSPACES: readonly WorkspaceId[] = Object.freeze(['timing', 'chart', 'publish']);
const SPACINGS: readonly TimelineViewState['spacing'][] = Object.freeze(['beats', 'seconds', 'floor']);
const TEMPO_POLICIES: readonly TempoEditPolicy[] = Object.freeze(['keepBeats', 'keepTime']);

/** Beat snap divisor bounds (custom divisors up to 1/1920, DESIGN.md §4.3). */
export const SNAP_DIV_MIN = 1;
export const SNAP_DIV_MAX = 1920;
/** X grid line count bounds (RPE "vertical lines"). */
export const X_GRID_MIN = 1;
export const X_GRID_MAX = 1000;
/** Time zoom bounds (px per beat). */
export const PX_PER_BEAT_MIN = 2;
export const PX_PER_BEAT_MAX = 12000;
/** Lane X half-range bounds (RPE px; 675 = the field's half width). */
export const LANE_HALF_RANGE_MIN = 20;
export const LANE_HALF_RANGE_MAX = 200000;
/** Bound for scroll positions (beats) and the lane centre (RPE px). */
const POSITION_LIMIT = 1e7;
/** Additive layers per property (RPE writes up to 4; imports may carry more). */
const LAYER_MAX = 255;

export const DEFAULT_SNAP: SnapState = Object.freeze({ div: 4, beatSnap: true, xGrid: 11, xSnap: true });

export const DEFAULT_TIMELINE: TimelineViewState = Object.freeze({
  pxPerBeat: 120,
  bottomBeat: 0,
  laneHalfRange: 675,
  laneCenterX: 0,
  followPlayhead: true,
  showMotionColumns: true,
  expandedColumn: null,
  spacing: 'beats',
  waveform: true,
  waveformOpacity: 0.4,
});

export const EMPTY_FILTERS: ViewFilterState = Object.freeze({
  hiddenLines: new Set<LineId>(),
  soloLines: new Set<LineId>(),
  lockedLines: new Set<LineId>(),
  mutedLayers: new Map<LineId, ReadonlySet<string>>(),
  dimOthers: 0,
  focusHitsoundLines: new Set<LineId>(),
});

// ---------------------------------------------------------------------------------------------------------------
// Normalisers (shared with the editor store setters)
// ---------------------------------------------------------------------------------------------------------------

/** `v` within lo..hi; -0 becomes 0 (Object.is equality would otherwise see a change where there is none). */
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v === 0 ? 0 : v;
}

function finiteOr(v: number | undefined, fallback: number): number {
  return v !== undefined && Number.isFinite(v) ? v : fallback;
}

function intIn(v: number | undefined, fallback: number, lo: number, hi: number): number {
  return clamp(Math.round(finiteOr(v, fallback)), lo, hi);
}

/** `base` with the defined fields of `p` applied and clamped to their bounds. */
export function normalizeSnap(p: Partial<SnapState>, base: SnapState = DEFAULT_SNAP): SnapState {
  return {
    div: intIn(p.div, base.div, SNAP_DIV_MIN, SNAP_DIV_MAX),
    beatSnap: p.beatSnap ?? base.beatSnap,
    xGrid: intIn(p.xGrid, base.xGrid, X_GRID_MIN, X_GRID_MAX),
    xSnap: p.xSnap ?? base.xSnap,
  };
}

/** `base` with the defined fields of `p` applied and clamped to their bounds (non-finite numbers are ignored). */
export function normalizeTimeline(
  p: Partial<TimelineViewState>,
  base: TimelineViewState = DEFAULT_TIMELINE,
): TimelineViewState {
  return {
    pxPerBeat: clamp(finiteOr(p.pxPerBeat, base.pxPerBeat), PX_PER_BEAT_MIN, PX_PER_BEAT_MAX),
    bottomBeat: clamp(finiteOr(p.bottomBeat, base.bottomBeat), -POSITION_LIMIT, POSITION_LIMIT),
    laneHalfRange: clamp(finiteOr(p.laneHalfRange, base.laneHalfRange), LANE_HALF_RANGE_MIN, LANE_HALF_RANGE_MAX),
    laneCenterX: clamp(finiteOr(p.laneCenterX, base.laneCenterX), -POSITION_LIMIT, POSITION_LIMIT),
    followPlayhead: p.followPlayhead ?? base.followPlayhead,
    showMotionColumns: p.showMotionColumns ?? base.showMotionColumns,
    expandedColumn: p.expandedColumn !== undefined ? p.expandedColumn : base.expandedColumn,
    spacing: p.spacing !== undefined && SPACINGS.includes(p.spacing) ? p.spacing : base.spacing,
    waveform: p.waveform ?? base.waveform,
    waveformOpacity: clamp(finiteOr(p.waveformOpacity, base.waveformOpacity), 0, 1),
  };
}

/** Same members (order ignored). */
export function sameSet<T>(a: ReadonlySet<T>, b: ReadonlySet<T>): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

/** Same lines with the same muted layers. */
export function sameMutedLayers(
  a: ReadonlyMap<LineId, ReadonlySet<string>>,
  b: ReadonlyMap<LineId, ReadonlySet<string>>,
): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const [line, layers] of a) {
    const other = b.get(line);
    if (other === undefined || !sameSet(layers, other)) return false;
  }
  return true;
}

/** `next` unless it has the same members as `base` (then `base`, so unchanged state keeps its identity). */
function keepSet<T>(next: ReadonlySet<T> | undefined, base: ReadonlySet<T>): ReadonlySet<T> {
  return next === undefined || sameSet(next, base) ? base : next;
}

/** `next` without lines that have no muted layer; `base` when the contents are the same. */
function keepMutedLayers(
  next: ReadonlyMap<LineId, ReadonlySet<string>> | undefined,
  base: ReadonlyMap<LineId, ReadonlySet<string>>,
): ReadonlyMap<LineId, ReadonlySet<string>> {
  if (next === undefined || next === base) return base;
  let muted = next;
  for (const layers of next.values()) {
    if (layers.size === 0) {
      muted = new Map([...next].filter(([, l]) => l.size > 0));
      break;
    }
  }
  return sameMutedLayers(muted, base) ? base : muted;
}

/**
 * `base` with the defined fields of `p` applied; dim clamped to 0..1, lines without muted layers dropped from
 * `mutedLayers`. A set or map with the same contents as `base`'s keeps `base`'s instance, so a toggle that ends
 * where it started (a new Set with the same members) is no change for the editor store and notifies nobody.
 */
export function normalizeFilters(p: Partial<ViewFilterState>, base: ViewFilterState = EMPTY_FILTERS): ViewFilterState {
  return {
    hiddenLines: keepSet(p.hiddenLines, base.hiddenLines),
    soloLines: keepSet(p.soloLines, base.soloLines),
    lockedLines: keepSet(p.lockedLines, base.lockedLines),
    mutedLayers: keepMutedLayers(p.mutedLayers, base.mutedLayers),
    dimOthers: clamp(finiteOr(p.dimOthers, base.dimOthers), 0, 1),
    focusHitsoundLines: keepSet(p.focusHitsoundLines, base.focusHitsoundLines),
  };
}

/** Hide, solo, muted layers and dim change the preview ("Preview differs from export" banner); lock and focus do not. */
export function filtersChangePreview(f: ViewFilterState): boolean {
  if (f.hiddenLines.size > 0 || f.soloLines.size > 0 || f.dimOthers > 0) return true;
  for (const layers of f.mutedLayers.values()) if (layers.size > 0) return true;
  return false;
}

/** Column identity (line, property, layer). */
export function sameColumn(a: ColumnRef | null, b: ColumnRef | null): boolean {
  if (a === b) return true;
  return a !== null && b !== null && a.line === b.line && a.prop === b.prop && a.layer === b.layer;
}

/** Columns without duplicates (first occurrence kept). */
export function uniqueColumns(cols: readonly ColumnRef[]): ColumnRef[] {
  const out: ColumnRef[] = [];
  for (const c of cols) if (!out.some((o) => sameColumn(o, c))) out.push(c);
  return out;
}

/** Ids without duplicates (first occurrence kept). */
export function uniqueIds<T extends number>(ids: readonly T[]): T[] {
  return [...new Set(ids)];
}

// ---------------------------------------------------------------------------------------------------------------
// Validation of untrusted JSON
// ---------------------------------------------------------------------------------------------------------------

/** Thrown inside `parse` on the first invalid field. */
class Invalid extends Error {
  override readonly name = 'ViewStateInvalid';
}

type Rec = Readonly<Record<string, unknown>>;

function isRec(v: unknown): v is Rec {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function rec(v: unknown, path: string): Rec {
  if (!isRec(v)) throw new Invalid(path);
  return v;
}

function bool(v: unknown, path: string): boolean {
  if (typeof v !== 'boolean') throw new Invalid(path);
  return v;
}

function num(v: unknown, path: string): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) throw new Invalid(path);
  return v;
}

function int(v: unknown, path: string): number {
  const n = num(v, path);
  if (!Number.isInteger(n)) throw new Invalid(path);
  return n;
}

function str(v: unknown, path: string): string {
  if (typeof v !== 'string') throw new Invalid(path);
  return v;
}

function arr(v: unknown, path: string): readonly unknown[] {
  if (!Array.isArray(v)) throw new Invalid(path);
  return v as readonly unknown[];
}

function lineId(v: unknown, path: string): LineId {
  const n = int(v, path);
  if (n < 0 || !Number.isSafeInteger(n)) throw new Invalid(path);
  return n as LineId;
}

function lineIds(v: unknown, path: string): LineId[] {
  return uniqueIds(arr(v, path).map((x, i) => lineId(x, `${path}[${String(i)}]`)));
}

function oneOf<T extends string>(v: unknown, values: readonly T[], path: string): T {
  if (typeof v !== 'string' || !(values as readonly string[]).includes(v)) throw new Invalid(path);
  return v as T;
}

function column(v: unknown, path: string): ColumnRef {
  const o = rec(v, path);
  const layer = int(o.layer, `${path}.layer`);
  if (layer < 0 || layer > LAYER_MAX) throw new Invalid(`${path}.layer`);
  return { line: lineId(o.line, `${path}.line`), prop: oneOf<LineProp>(o.prop, LINE_PROPS, `${path}.prop`), layer };
}

function snapOf(v: unknown): SnapState {
  const o = rec(v, 'snap');
  return normalizeSnap({
    div: int(o.div, 'snap.div'),
    beatSnap: bool(o.beatSnap, 'snap.beatSnap'),
    xGrid: int(o.xGrid, 'snap.xGrid'),
    xSnap: bool(o.xSnap, 'snap.xSnap'),
  });
}

function timelineOf(v: unknown): TimelineViewState {
  const o = rec(v, 'timeline');
  const expanded = o.expandedColumn;
  if (expanded !== null && typeof expanded !== 'string') throw new Invalid('timeline.expandedColumn');
  const pxPerBeat = num(o.pxPerBeat, 'timeline.pxPerBeat');
  const laneHalfRange = num(o.laneHalfRange, 'timeline.laneHalfRange');
  if (pxPerBeat <= 0) throw new Invalid('timeline.pxPerBeat');
  if (laneHalfRange <= 0) throw new Invalid('timeline.laneHalfRange');
  return normalizeTimeline({
    pxPerBeat,
    bottomBeat: num(o.bottomBeat, 'timeline.bottomBeat'),
    laneHalfRange,
    laneCenterX: num(o.laneCenterX, 'timeline.laneCenterX'),
    followPlayhead: bool(o.followPlayhead, 'timeline.followPlayhead'),
    showMotionColumns: bool(o.showMotionColumns, 'timeline.showMotionColumns'),
    expandedColumn: expanded,
    spacing: oneOf(o.spacing, SPACINGS, 'timeline.spacing'),
    waveform: bool(o.waveform, 'timeline.waveform'),
    waveformOpacity: num(o.waveformOpacity, 'timeline.waveformOpacity'),
  });
}

function filtersOf(v: unknown): PersistedViewState['filters'] {
  const o = rec(v, 'filters');
  const muted: (readonly [LineId, readonly string[]])[] = [];
  const seen = new Set<LineId>();
  arr(o.muted, 'filters.muted').forEach((entry, i) => {
    const path = `filters.muted[${String(i)}]`;
    const pair = arr(entry, path);
    if (pair.length !== 2) throw new Invalid(path);
    const line = lineId(pair[0], `${path}[0]`);
    const layers = [...new Set(arr(pair[1], `${path}[1]`).map((l, j) => str(l, `${path}[1][${String(j)}]`)))];
    if (seen.has(line)) throw new Invalid(path);
    seen.add(line);
    if (layers.length > 0) muted.push([line, layers]);
  });
  return {
    hidden: lineIds(o.hidden, 'filters.hidden'),
    solo: lineIds(o.solo, 'filters.solo'),
    locked: lineIds(o.locked, 'filters.locked'),
    muted,
    dimOthers: clamp(num(o.dimOthers, 'filters.dimOthers'), 0, 1),
    focusHitsound: lineIds(o.focusHitsound, 'filters.focusHitsound'),
  };
}

function parseStrict(json: unknown): PersistedViewState {
  const o = rec(json, '$');
  if (o.v !== undefined && o.v !== VIEW_STATE_VERSION) throw new Invalid('v');
  const active = o.activeLine;
  return {
    workspace: oneOf(o.workspace, WORKSPACES, 'workspace'),
    activeLine: active === null ? null : lineId(active, 'activeLine'),
    pinnedLanes: lineIds(o.pinnedLanes, 'pinnedLanes'),
    pinnedColumns: uniqueColumns(
      arr(o.pinnedColumns, 'pinnedColumns').map((c, i) => column(c, `pinnedColumns[${String(i)}]`)),
    ),
    alsoShow: lineIds(o.alsoShow, 'alsoShow'),
    snap: snapOf(o.snap),
    timeline: timelineOf(o.timeline),
    playheadBeat: Math.max(0, num(o.playheadBeat, 'playheadBeat')),
    filters: filtersOf(o.filters),
    ...(o.tempoPolicy === undefined ? {} : { tempoPolicy: oneOf(o.tempoPolicy, TEMPO_POLICIES, 'tempoPolicy') }),
  };
}

/** Validates untrusted JSON (a parsed value or a JSON string); null when invalid or from another format version. */
function parse(json: unknown): PersistedViewState | null {
  let value = json;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }
  try {
    return parseStrict(value);
  } catch (e) {
    if (e instanceof Invalid) return null;
    throw e;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Snapshot / restore
// ---------------------------------------------------------------------------------------------------------------

/** The editor-store fields that are per-project view state. */
export type ViewStatePatch = Pick<
  EditorStoreState,
  | 'workspace'
  | 'activeLine'
  | 'pinnedLanes'
  | 'pinnedColumns'
  | 'alsoShow'
  | 'snap'
  | 'timeline'
  | 'filters'
  | 'tempoPolicy'
>;

function sortedIds(ids: Iterable<LineId>): LineId[] {
  return [...ids].sort((a, b) => a - b);
}

/** The persisted view of an editor state (`playheadBeat` comes from the transport). */
function snapshot(state: ViewStatePatch, playheadBeat: number): PersistedViewState {
  const f = state.filters;
  const muted: (readonly [LineId, readonly string[]])[] = [];
  for (const line of sortedIds(f.mutedLayers.keys())) {
    const layers = f.mutedLayers.get(line);
    if (layers !== undefined && layers.size > 0) muted.push([line, [...layers].sort()]);
  }
  return {
    workspace: state.workspace,
    activeLine: state.activeLine,
    pinnedLanes: [...state.pinnedLanes],
    pinnedColumns: state.pinnedColumns.map((c) => ({ line: c.line, prop: c.prop, layer: c.layer })),
    alsoShow: [...state.alsoShow],
    snap: normalizeSnap(state.snap),
    timeline: normalizeTimeline(state.timeline),
    playheadBeat: Number.isFinite(playheadBeat) ? Math.max(0, playheadBeat) : 0,
    filters: {
      hidden: sortedIds(f.hiddenLines),
      solo: sortedIds(f.soloLines),
      locked: sortedIds(f.lockedLines),
      muted,
      dimOthers: clamp(finiteOr(f.dimOthers, 0), 0, 1),
      focusHitsound: sortedIds(f.focusHitsoundLines),
    },
    tempoPolicy: state.tempoPolicy,
  };
}

/** Editor-store fields restored from a persisted view. */
function restore(view: PersistedViewState): ViewStatePatch {
  const f = view.filters;
  return {
    workspace: view.workspace,
    activeLine: view.activeLine,
    pinnedLanes: uniqueIds(view.pinnedLanes),
    pinnedColumns: uniqueColumns(view.pinnedColumns),
    alsoShow: uniqueIds(view.alsoShow),
    snap: normalizeSnap(view.snap),
    timeline: normalizeTimeline(view.timeline),
    filters: normalizeFilters({
      hiddenLines: new Set(f.hidden),
      soloLines: new Set(f.solo),
      lockedLines: new Set(f.locked),
      mutedLayers: new Map(
        f.muted.filter(([, layers]) => layers.length > 0).map(([line, layers]) => [line, new Set(layers)]),
      ),
      dimOthers: f.dimOthers,
      focusHitsoundLines: new Set(f.focusHitsound),
    }),
    tempoPolicy: view.tempoPolicy ?? 'keepBeats',
  };
}

/** Drops references to lines the document does not have (the active line falls back to the first line). */
function sanitize(view: PersistedViewState, doc: ChartDoc): PersistedViewState {
  const has = (id: LineId): boolean => doc.lines.has(id);
  const keep = (ids: readonly LineId[]): LineId[] => ids.filter(has);
  const f = view.filters;
  const active = view.activeLine !== null && has(view.activeLine) ? view.activeLine : (doc.lineOrder[0] ?? null);
  return {
    ...view,
    activeLine: active,
    pinnedLanes: keep(view.pinnedLanes),
    pinnedColumns: view.pinnedColumns.filter((c) => has(c.line)),
    alsoShow: keep(view.alsoShow),
    filters: {
      hidden: keep(f.hidden),
      solo: keep(f.solo),
      locked: keep(f.locked),
      muted: f.muted.filter(([line]) => has(line)),
      dimOthers: f.dimOthers,
      focusHitsound: keep(f.focusHitsound),
    },
  };
}

/** The JSON written by the app (tagged with the format version). */
function toJson(view: PersistedViewState): Json {
  const s = snapshot(restore(view), view.playheadBeat);
  return {
    v: VIEW_STATE_VERSION,
    workspace: s.workspace,
    activeLine: s.activeLine,
    pinnedLanes: [...s.pinnedLanes],
    pinnedColumns: s.pinnedColumns.map((c) => ({ line: c.line, prop: c.prop, layer: c.layer })),
    alsoShow: [...s.alsoShow],
    snap: { ...s.snap },
    timeline: { ...s.timeline },
    playheadBeat: s.playheadBeat,
    filters: {
      hidden: [...s.filters.hidden],
      solo: [...s.filters.solo],
      locked: [...s.filters.locked],
      muted: s.filters.muted.map(([line, layers]) => [line, [...layers]]),
      dimOthers: s.filters.dimOthers,
      focusHitsound: [...s.filters.focusHitsound],
    },
    tempoPolicy: s.tempoPolicy ?? 'keepBeats',
  };
}

export interface ViewStateApiExt extends ViewStateApi {
  readonly VERSION: number;
  /** The persisted view of an editor state (`playheadBeat` from the transport). */
  snapshot(state: ViewStatePatch, playheadBeat: number): PersistedViewState;
  /** Editor-store fields of a persisted view (Sets and Maps rebuilt, values clamped). */
  restore(view: PersistedViewState): ViewStatePatch;
  /** Drops references to lines missing from `doc`. */
  sanitize(view: PersistedViewState, doc: ChartDoc): PersistedViewState;
  /** JSON for persistence, tagged with the format version. */
  toJson(view: PersistedViewState): Json;
}

export const ViewState: ViewStateApiExt = {
  VERSION: VIEW_STATE_VERSION,
  parse,
  snapshot,
  restore,
  sanitize,
  toJson,
};
